import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, ArrowLeft, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const SIDE_IMAGE = 'https://customer-assets.emergentagent.com/job_2b2cda0c-551c-44d2-8a1b-28bbeca209e6/artifacts/dc07ctdz_ChatGPT%20Image%20Jun%2024%2C%202026%2C%2012_18_27%20PM.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    if (user.is_admin) navigate('/admin', { replace: true });
    else if (user.verification_status === 'approved') navigate('/profile', { replace: true });
    else navigate('/verification', { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Something went wrong. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Side Image */}
      <div className="hidden lg:block lg:w-[40%] relative">
        <img src={SIDE_IMAGE} alt="Highway logistics" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-slate-900/20" />
        <div className="absolute bottom-12 left-12 right-12 z-10">
          <div className="flex items-center gap-2.5 mb-6">
            <img src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG" alt="Logo" className="h-8 w-auto" />
            <span className="text-white text-lg font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>StraightConnect</span>
          </div>
          <h2 className="text-2xl font-bold text-white leading-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>The network that moves with you.</h2>
          <p className="text-slate-300 text-sm">Verified carriers. Real opportunities. One platform built for non-CDL logistics.</p>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12 bg-white">
        <div className="w-full max-w-[420px]">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-8 transition-colors" data-testid="login-back-home-link">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>

          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <img src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG" alt="Logo" className="h-8 w-auto" />
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>StraightConnect</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Sign in</h2>
          <p className="text-slate-500 text-sm mb-8">Enter your credentials to continue</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3.5 mb-6 flex items-start gap-3" data-testid="login-error-alert">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-slate-900 mb-2">Email address</label>
              <input id="login-email" type="email" autoComplete="username email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" className="w-full h-12 px-4 rounded-md border border-slate-200 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-shadow" data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="login-password" className="block text-sm font-medium text-slate-900">Password</label>
                <Link to="/reset-password" className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline underline-offset-4 transition-colors" data-testid="login-forgot-password-link">Forgot password?</Link>
              </div>
              <div className="relative">
                <input id="login-password" type={showPw ? 'text' : 'password'} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password" className="w-full h-12 px-4 pr-12 rounded-md border border-slate-200 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-shadow" data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1" data-testid="login-toggle-password-visibility">
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-md transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-testid="login-submit-button">
              {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Signing in...</> : 'Sign in'}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-8">
            Don&apos;t have an account?{' '}
            <Link to="/join" className="font-semibold text-blue-600 hover:text-blue-700 hover:underline underline-offset-4 transition-colors" data-testid="login-join-network-link">Join the network</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
